import { VISIT_FIELD_KEYS } from "@/components/MedicalChronology/Visits/Visits.types";
import { normalizeVisitDetailCategory } from "@/components/MedicalChronology/Visits/utils/medVisitDetailNormalize";
import { areCitationListsEqual } from "@/components/MedicalChronology/Visits/utils/visitCitations";
import type { MedVisit, MedVisitDetailResponse } from "@/types/medchron";
import { MED_SUMMARY_FIELD_KEYS } from "@/types/summary";
import type { MedSummaryData } from "@/types/summary";
import { diffWords } from "diff";

import type {
  ChronologyDiffResult,
  DetailCategoryDiff,
  FieldDiff,
  SummaryFieldDiff,
  TextDiffSegment,
  VisitDiffEntry,
} from "./types";
import { VisitDiffStatus } from "./types";
import { matchVisits } from "./visitMatching";

const toComparable = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(",");
  return String(value);
};

export const toTextSegments = (oldText: string | null, newText: string | null): TextDiffSegment[] =>
  diffWords(oldText ?? "", newText ?? "").map((part) => ({
    value: part.value,
    added: part.added,
    removed: part.removed,
  }));

export const diffVisitFields = (oldVisit: MedVisit, newVisit: MedVisit): FieldDiff[] =>
  VISIT_FIELD_KEYS.map((field) => {
    const oldValue = oldVisit[field];
    const newValue = newVisit[field];
    return {
      field,
      oldValue,
      newValue,
      changed: toComparable(oldValue) !== toComparable(newValue),
    };
  });

type DetailMatcher = (a: MedVisitDetailResponse, b: MedVisitDetailResponse) => boolean;

/**
 * Matches old/new visit-detail entries across two versions and diffs each pair.
 *
 * Categories are free text, so a rename (or a whitespace/casing tweak) produces a different
 * raw string on each side. A plain exact-key match would treat that as one category being
 * removed and an unrelated one being added. Instead this runs two matching passes, in priority
 * order, before falling back to unmatched removed/added entries:
 *   1. category equal after `normalizeVisitDetailCategory` (catches whitespace/punctuation/case-only diffs)
 *   2. body text identical after trimming (catches an outright category rename with unchanged content)
 */
export const diffVisitDetails = (oldVisit: MedVisit, newVisit: MedVisit): DetailCategoryDiff[] => {
  const oldDetails = oldVisit.med_visit_details;
  const newDetails = newVisit.med_visit_details;
  const oldMatchedNewIndex: (number | null)[] = oldDetails.map(() => null);
  const newUsed = newDetails.map(() => false);

  const runMatchPass = (isMatch: DetailMatcher): void => {
    oldDetails.forEach((oldDetail, oldIndex) => {
      if (oldMatchedNewIndex[oldIndex] !== null) return;
      const newIndex = newDetails.findIndex(
        (newDetail, i) => !newUsed[i] && isMatch(oldDetail, newDetail)
      );
      if (newIndex !== -1) {
        oldMatchedNewIndex[oldIndex] = newIndex;
        newUsed[newIndex] = true;
      }
    });
  };

  runMatchPass(
    (a, b) =>
      normalizeVisitDetailCategory(a.visit_detail_category) ===
      normalizeVisitDetailCategory(b.visit_detail_category)
  );

  // Exact (not fuzzy) content match, so genuinely unrelated categories that happen to share
  // wording are never paired -- only a rename with identical carried-over content is.
  runMatchPass((a, b) => a.visit_details.trim() === b.visit_details.trim());

  const pairs: Array<{
    oldDetail: MedVisitDetailResponse | null;
    newDetail: MedVisitDetailResponse | null;
  }> = oldDetails.map((oldDetail, oldIndex) => {
    const newIndex = oldMatchedNewIndex[oldIndex];
    return { oldDetail, newDetail: newIndex !== null ? newDetails[newIndex] : null };
  });
  newDetails.forEach((newDetail, newIndex) => {
    if (!newUsed[newIndex]) pairs.push({ oldDetail: null, newDetail });
  });

  return pairs.map(({ oldDetail, newDetail }): DetailCategoryDiff => {
    const oldText = oldDetail?.visit_details ?? null;
    const newText = newDetail?.visit_details ?? null;
    const category = newDetail?.visit_detail_category ?? oldDetail?.visit_detail_category ?? "";
    const previousCategory =
      oldDetail && newDetail && oldDetail.visit_detail_category !== newDetail.visit_detail_category
        ? oldDetail.visit_detail_category
        : null;
    return {
      category,
      previousCategory,
      oldText,
      newText,
      changed: (oldText ?? "") !== (newText ?? "") || previousCategory !== null,
      segments: toTextSegments(oldText, newText),
    };
  });
};

export const diffSummary = (
  oldSummary: MedSummaryData | null,
  newSummary: MedSummaryData | null
): SummaryFieldDiff[] =>
  MED_SUMMARY_FIELD_KEYS.map((field) => {
    const oldValue = oldSummary ? (oldSummary[field] as string | null) : null;
    const newValue = newSummary ? (newSummary[field] as string | null) : null;
    return {
      field,
      oldValue,
      newValue,
      changed: (oldValue ?? "") !== (newValue ?? ""),
      segments: toTextSegments(oldValue, newValue),
    };
  });

export const computeChronologyDiff = (
  oldVisits: MedVisit[],
  newVisits: MedVisit[],
  oldSummary: MedSummaryData | null,
  newSummary: MedSummaryData | null
): ChronologyDiffResult => {
  const { removed, added, matched, matchMode } = matchVisits(oldVisits, newVisits);

  const visitDiffs: VisitDiffEntry[] = [
    ...removed.map(
      (v): VisitDiffEntry => ({
        visitId: v.visit_id,
        status: VisitDiffStatus.Removed,
        oldVisit: v,
        newVisit: null,
        fieldDiffs: [],
        citationsChanged: false,
        detailDiffs: [],
      })
    ),
    ...added.map(
      (v): VisitDiffEntry => ({
        visitId: v.visit_id,
        status: VisitDiffStatus.Added,
        oldVisit: null,
        newVisit: v,
        fieldDiffs: [],
        citationsChanged: false,
        detailDiffs: [],
      })
    ),
    ...matched.map(([oldVisit, newVisit]): VisitDiffEntry => {
      const fieldDiffs = diffVisitFields(oldVisit, newVisit);
      // Content-matched pairs have different visit_ids and already share canonical citations.
      const citationsChanged =
        oldVisit.visit_id === newVisit.visit_id &&
        !areCitationListsEqual(oldVisit.citations, newVisit.citations);
      const detailDiffs = diffVisitDetails(oldVisit, newVisit);
      const hasChanges =
        fieldDiffs.some((f) => f.changed) || citationsChanged || detailDiffs.some((d) => d.changed);
      return {
        visitId: oldVisit.visit_id,
        status: hasChanges ? VisitDiffStatus.Modified : VisitDiffStatus.Unchanged,
        oldVisit,
        newVisit,
        fieldDiffs,
        citationsChanged,
        detailDiffs,
      };
    }),
  ];

  visitDiffs.sort((a, b) => {
    const dateA = a.newVisit?.visit_date ?? a.oldVisit?.visit_date ?? "";
    const dateB = b.newVisit?.visit_date ?? b.oldVisit?.visit_date ?? "";
    return dateA.localeCompare(dateB);
  });

  return { visitDiffs, summaryDiffs: diffSummary(oldSummary, newSummary), matchMode };
};
